import { useCallback, useEffect, useMemo, useState } from "react";
import { ConfigurableOption, Value } from "../../../../../types/VenueGroup";
import { Option, Value as BookingValue } from "../../../../../types/bookings";
import { EDSButton } from "../../../../eds/EDSButton";
import { Checkbox } from "../../../../ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "../../../../ui/dialog";
import { getCurrencySymbol } from "../../../../../utils/number";

type SelectedState = Record<string, Record<number, boolean>>;

type OptionValidity = {
  valid: boolean;
  message?: string;
};

type ConfigurableOptionsModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemName: string;
  basePrice: number;
  configurableOptions: ConfigurableOption[];
  existingOptions?: Option[];
  currency: string;
  onConfirm: (options: Option[], optionsPrice: number) => void;
};

const buildInitialSelections = (
  configurableOptions: ConfigurableOption[],
  existingOptions?: Option[]
): SelectedState => {
  const state: SelectedState = {};

  configurableOptions.forEach((option) => {
    state[option.label] = {};

    const existing = existingOptions?.find((o) => o.label === option.label);

    if (!existing?.values?.length) {
      option.values.forEach((_, index) => {
        state[option.label][index] = false;
      });
      return;
    }

    const remainingExisting = [...existing.values];

    option.values.forEach((value, index) => {
      const matchIndex = remainingExisting.findIndex((v) => {
        if (v.plu !== undefined && value.plu !== undefined) {
          return v.label === value.label && v.plu === value.plu;
        }
        return v.label === value.label;
      });

      if (matchIndex !== -1) {
        state[option.label][index] = true;
        remainingExisting.splice(matchIndex, 1);
      } else {
        state[option.label][index] = false;
      }
    });
  });

  return state;
};

const getValidationMessage = (
  option: ConfigurableOption,
  numSelected: number
): OptionValidity => {
  const { min, max } = option;

  if (min && min === max && numSelected !== min) {
    const label = min === 1 ? "an option" : `${min} options`;
    return { valid: false, message: `Please choose ${label}` };
  }

  if (min && numSelected < min) {
    const label = min === 1 ? "option" : "options";
    return {
      valid: false,
      message: `Please choose at least ${min} ${label}`
    };
  }

  if (max && numSelected > max) {
    const label = max === 1 ? "option" : "options";
    return {
      valid: false,
      message: `You may choose up to ${max} ${label}`
    };
  }

  return { valid: true };
};

export const ConfigurableOptionsModal = ({
  open,
  onOpenChange,
  itemName,
  basePrice,
  configurableOptions,
  existingOptions,
  currency,
  onConfirm
}: ConfigurableOptionsModalProps) => {
  const [selections, setSelections] = useState<SelectedState>({});
  const [validity, setValidity] = useState<Record<string, OptionValidity>>({});
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const currencySymbol = getCurrencySymbol(currency);

  useEffect(() => {
    if (open) {
      setSelections(
        buildInitialSelections(configurableOptions, existingOptions)
      );
      setValidity({});
      setHasAttemptedSubmit(false);
    }
  }, [open, configurableOptions, existingOptions]);

  const toggleValue = useCallback((optionLabel: string, valueIndex: number) => {
    setSelections((prev) => ({
      ...prev,
      [optionLabel]: {
        ...prev[optionLabel],
        [valueIndex]: !prev[optionLabel]?.[valueIndex]
      }
    }));
  }, []);

  const optionsPrice = useMemo(() => {
    let total = 0;
    configurableOptions.forEach((option) => {
      option.values.forEach((value, index) => {
        if (selections[option.label]?.[index]) {
          total += value.price_change ?? 0;
        }
      });
    });
    return total;
  }, [configurableOptions, selections]);

  const checkValidity = useCallback((): boolean => {
    const newValidity: Record<string, OptionValidity> = {};
    let allValid = true;

    configurableOptions.forEach((option) => {
      const numSelected = Object.values(selections[option.label] ?? {}).filter(
        Boolean
      ).length;

      const result = getValidationMessage(option, numSelected);
      newValidity[option.label] = result;

      if (!result.valid) {
        allValid = false;
      }
    });

    setValidity(newValidity);
    return allValid;
  }, [configurableOptions, selections]);

  const handleConfirm = useCallback(() => {
    setHasAttemptedSubmit(true);

    if (!checkValidity()) {
      return;
    }

    const options: Option[] = configurableOptions.map((option) => {
      const selectedValues: BookingValue[] = option.values
        .filter((_, index) => selections[option.label]?.[index])
        .map((value) => ({
          label: value.label,
          price_change: value.price_change ?? 0,
          plu: value.plu,
          selected: true
        }));

      return {
        label: option.label,
        description: option.description,
        min: option.min,
        max: option.max,
        values: selectedValues
      };
    });

    onConfirm(options, optionsPrice);
  }, [configurableOptions, selections, optionsPrice, checkValidity, onConfirm]);

  useEffect(() => {
    if (hasAttemptedSubmit) {
      checkValidity();
    }
  }, [hasAttemptedSubmit, selections, checkValidity]);

  const getCountLabel = (option: ConfigurableOption): string => {
    const numSelected = Object.values(selections[option.label] ?? {}).filter(
      Boolean
    ).length;

    if (option.min === option.max && option.min) {
      return `${numSelected}/${option.min} selected`;
    }
    if (option.min && option.max) {
      return `${numSelected} selected (${option.min}-${option.max} required)`;
    }
    if (option.min) {
      return `${numSelected} selected (min ${option.min})`;
    }
    if (option.max) {
      return `${numSelected} selected (max ${option.max})`;
    }
    return `${numSelected} selected`;
  };

  const formatPriceChange = (value: Value): string => {
    if (!value.price_change) return "";
    const sign = value.price_change > 0 ? "+" : "-";
    return ` (${sign}${currencySymbol}${Math.abs(value.price_change).toFixed(2)})`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] lg:max-w-[700px] max-h-[90vh] min-h-[300px] overflow-hidden flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle>
            {itemName} - {currencySymbol}
            {(basePrice + optionsPrice).toFixed(2)}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-6 py-4 flex-1 overflow-y-auto min-h-0">
          {configurableOptions.map((option) => (
            <div key={option.label} className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-[15px] m-0">
                    {option.label}
                  </p>
                  <span className="text-xs text-muted-foreground">
                    {getCountLabel(option)}
                  </span>
                </div>
                {option.description && (
                  <p className="text-sm text-muted-foreground m-0">
                    {option.description}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {option.values.map((value, valueIndex) => (
                  <label
                    key={`${option.label}-${valueIndex}`}
                    className="flex items-center gap-2 cursor-pointer min-h-11 p-2 hover:bg-muted/50 rounded border"
                  >
                    <Checkbox
                      checked={selections[option.label]?.[valueIndex] ?? false}
                      onCheckedChange={() =>
                        toggleValue(option.label, valueIndex)
                      }
                      className="h-5 w-5"
                    />
                    <span className="text-sm select-none flex-1">
                      {value.label}
                      {formatPriceChange(value) && (
                        <span className="text-muted-foreground">
                          {formatPriceChange(value)}
                        </span>
                      )}
                    </span>
                  </label>
                ))}
              </div>

              {hasAttemptedSubmit && !validity[option.label]?.valid && (
                <p className="text-sm text-red-600 m-0">
                  {validity[option.label]?.message}
                </p>
              )}
            </div>
          ))}
        </div>

        <DialogFooter className="shrink-0">
          <EDSButton variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </EDSButton>
          <EDSButton variant="default" onClick={handleConfirm}>
            Confirm Options
          </EDSButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConfigurableOptionsModal;
